import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../firebase";

const API =
  process.env.REACT_APP_SELLER_API ||
  "https://gallery.pitics.com";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

export default function SellerPayments() {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    deliveredOrders: 0,
    deliveredAmount: 0,
    eligibleOrders: 0,
    eligibleAmount: 0
  });
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState("");

  const loadPayments = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      const res = await fetch(`${API}/api/seller/payments`, {
        headers: {
          "X-Firebase-Authorization": token
        }
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(
          data.error || "Failed to load payments"
        );
      }

      setPayments(
        Array.isArray(data.payments)
          ? data.payments
          : []
      );

      setSummary({
        deliveredOrders: Number(
          data.summary?.deliveredOrders || 0
        ),
        deliveredAmount: Number(
          data.summary?.deliveredAmount || 0
        ),
        eligibleOrders: Number(
          data.summary?.eligibleOrders || 0
        ),
        eligibleAmount: Number(
          data.summary?.eligibleAmount || 0
        )
      });
    } catch (err) {
      console.error(err);
      alert(
        err.message ||
        "Failed to load seller payments"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, []);

  const processPayment = async (sellerOrderId) => {
    if (!sellerOrderId) return;

    if (!window.confirm(
      "Mark this delivered order as payment processed? It will no longer appear in this payment list."
    )) return;

    try {
      setProcessingId(sellerOrderId);

      const token =
        await auth.currentUser.getIdToken();

      const res = await fetch(
        `${API}/api/seller/payments/${encodeURIComponent(
          sellerOrderId
        )}/process`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firebase-Authorization": token
          }
        }
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(
          data.error ||
          "Failed to mark payment processed"
        );
      }

      await loadPayments();
    } catch (err) {
      console.error(err);
      alert(
        err.message ||
        "Payment processing update failed"
      );
    } finally {
      setProcessingId("");
    }
  };

  const sortedPayments = useMemo(
    () =>
      [...payments].sort((a, b) => {
        const ad = a.deliveredAt
          ? new Date(a.deliveredAt).getTime()
          : 0;
        const bd = b.deliveredAt
          ? new Date(b.deliveredAt).getTime()
          : 0;
        return bd - ad;
      }),
    [payments]
  );

  if (loading) {
    return <div style={styles.card}>Loading payments...</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={{ margin: 0 }}>💰 Payments</h2>
          <div style={styles.muted}>
            Delivered orders become eligible for payment
            processing 7 days after delivery.
          </div>
        </div>
        <button onClick={loadPayments} style={styles.secondaryBtn}>
          Refresh
        </button>
      </div>

      <div style={styles.summaryGrid}>
        <Summary label="Delivered Orders" value={summary.deliveredOrders} />
        <Summary label="Delivered Amount" value={money(summary.deliveredAmount)} />
        <Summary label="Eligible for Payment" value={summary.eligibleOrders} />
        <Summary label="Eligible Amount" value={money(summary.eligibleAmount)} />
      </div>

      {sortedPayments.length === 0 ? (
        <div style={styles.card}>
          <strong>No unprocessed delivered orders.</strong>
          <div style={styles.muted}>
            Orders already marked as payment processed are
            automatically excluded.
          </div>
        </div>
      ) : (
        <div style={styles.list}>
          {sortedPayments.map((p) => (
            <div key={p.sellerOrderId} style={styles.card}>
              <div style={styles.row}>
                <div>
                  <div style={styles.orderId}>
                    {p.sellerOrderId}
                  </div>
                  <div style={styles.muted}>
                    Parent Order: {p.orderId || "-"}
                  </div>
                </div>
                <div style={styles.amount}>
                  {money(p.amount)}
                </div>
              </div>

              <div style={styles.infoGrid}>
                <Info
                  label="Delivered"
                  value={
                    p.deliveredAt
                      ? new Date(p.deliveredAt).toLocaleString("en-IN")
                      : "-"
                  }
                />
                <Info
                  label="Payment eligibility"
                  value={
                    p.eligibleForPayment
                      ? "Yes — 7 days completed"
                      : `${p.daysRemaining} day(s) remaining`
                  }
                />
                <Info
                  label="Invoice"
                  value={p.invoiceNumber || "-"}
                />
                <Info
                  label="Status"
                  value={p.paymentStatus || "Pending"}
                />
              </div>

              <div style={styles.actionRow}>
                <button
                  disabled={
                    !p.eligibleForPayment ||
                    processingId === p.sellerOrderId
                  }
                  onClick={() =>
                    processPayment(p.sellerOrderId)
                  }
                  style={{
                    ...styles.primaryBtn,
                    opacity:
                      !p.eligibleForPayment ||
                      processingId === p.sellerOrderId
                        ? 0.5
                        : 1
                  }}
                >
                  {processingId === p.sellerOrderId
                    ? "Saving..."
                    : p.eligibleForPayment
                    ? "Mark Payment Processed"
                    : "Available After 7 Days"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div style={styles.summaryCard}>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <span style={styles.label}>{label}</span>
      <div>{value}</div>
    </div>
  );
}

const styles = {
  page: { display: "grid", gap: 18 },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit,minmax(190px,1fr))",
    gap: 14
  },
  summaryCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 20,
    padding: 20,
    boxShadow: "0 8px 24px rgba(0,0,0,.05)"
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 22,
    padding: 22,
    boxShadow: "0 8px 24px rgba(0,0,0,.05)"
  },
  list: { display: "grid", gap: 14 },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap"
  },
  orderId: { fontWeight: 900, fontSize: 16 },
  amount: { fontWeight: 900, fontSize: 20 },
  infoGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit,minmax(180px,1fr))",
    gap: 14,
    marginTop: 18
  },
  actionRow: {
    marginTop: 16,
    display: "flex",
    justifyContent: "flex-end"
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 900,
    color: "#6b7280",
    marginBottom: 5
  },
  value: {
    fontSize: 24,
    fontWeight: 900,
    marginTop: 8
  },
  muted: {
    color: "#6b7280",
    marginTop: 5,
    fontSize: 13
  },
  primaryBtn: {
    border: "none",
    borderRadius: 14,
    padding: "12px 16px",
    background: "#111827",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer"
  },
  secondaryBtn: {
    border: "none",
    borderRadius: 14,
    padding: "12px 16px",
    background: "#e5e7eb",
    color: "#111827",
    fontWeight: 900,
    cursor: "pointer"
  }
};
